import { DETECT_FLASH_TATTOO_DESIGN_ENDPOINT } from '../config/endpoints'
import { storage } from './firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

export interface BoundingBox {
  x: number // left
  y: number // top
  width: number
  height: number
}

export interface BackendDetectionResponse {
  bounding_box: [number, number, number, number] | null // [x1, y1, x2, y2] or null
}

export interface FlashCropAnalysis {
  isValid: boolean
  boundingBox: BoundingBox
  imageDimensions: { width: number; height: number }
  padding: {
    top: { value: number; isValid: boolean; percentage: number; needsMore: boolean }
    right: { value: number; isValid: boolean; percentage: number; needsMore: boolean }
    bottom: { value: number; isValid: boolean; percentage: number; needsMore: boolean }
    left: { value: number; isValid: boolean; percentage: number; needsMore: boolean }
  }
  confidence: number
  optimalCrop?: {
    x: number
    y: number
    width: number
    height: number
    appliedPadding: {
      top: number
      right: number
      bottom: number
      left: number
    }
  }
}

export interface ImageDimensions {
  width: number
  height: number
}

/**
 * Convert a data URL to a Blob
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  
  return new Blob([u8arr], { type: mime })
}

/**
 * Upload a data URL to Firebase Storage temporarily and return the download URL
 */
async function uploadDataUrlToTempStorage(dataUrl: string): Promise<string> {
  console.log('[uploadDataUrlToTempStorage] Converting data URL to blob...')
  const blob = dataUrlToBlob(dataUrl)
  console.log('[uploadDataUrlToTempStorage] Blob size:', (blob.size / 1024 / 1024).toFixed(2), 'MB')
  
  // Create a unique temporary path
  const timestamp = Date.now()
  const randomId = Math.random().toString(36).substring(2, 15)
  const tempPath = `temp-crop-analysis/${timestamp}-${randomId}.jpg`
  
  console.log('[uploadDataUrlToTempStorage] Uploading to:', tempPath)
  const storageRef = ref(storage, tempPath)
  
  const metadata = {
    contentType: blob.type || 'image/jpeg',
    customMetadata: {
      purpose: 'crop-analysis',
      uploadedAt: new Date().toISOString()
    }
  }
  
  const snapshot = await uploadBytes(storageRef, blob, metadata)
  console.log('[uploadDataUrlToTempStorage] ✅ Upload successful')
  
  const downloadUrl = await getDownloadURL(snapshot.ref)
  console.log('[uploadDataUrlToTempStorage] Download URL:', downloadUrl.substring(0, 80) + '...')
  
  return downloadUrl
}

/**
 * Get image dimensions from a URL
 */
export function getImageDimensions(imageUrl: string): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }
    img.src = imageUrl
  })
}

/**
 * Crop an image to specified dimensions and return as data URL
 */
export function cropImageToDataUrl(
  imageUrl: string,
  cropX: number,
  cropY: number,
  cropWidth: number,
  cropHeight: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = cropWidth
        canvas.height = cropHeight
        
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }
        
        // Draw the cropped portion of the image
        ctx.drawImage(
          img,
          cropX, cropY, cropWidth, cropHeight,  // Source rectangle
          0, 0, cropWidth, cropHeight            // Destination rectangle
        )
        
        // Convert to data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95)
        resolve(dataUrl)
      } catch (error) {
        reject(error)
      }
    }
    
    img.onerror = () => {
      reject(new Error('Failed to load image for cropping'))
    }
    
    img.src = imageUrl
  })
}

/**
 * Analyze flash crop using backend API
 */
export async function analyzeFlashCrop(imageUrl: string): Promise<FlashCropAnalysis> {
  console.log('[analyzeFlashCrop] Starting analysis for image:', imageUrl.substring(0, 100) + '...')
  
  try {
    // Get image dimensions first
    console.log('[analyzeFlashCrop] Getting image dimensions...')
    const imageDimensions = await getImageDimensions(imageUrl)
    console.log('[analyzeFlashCrop] Image dimensions:', imageDimensions)
    
    // Check if this is a data URL (base64) - upload to storage first
    let urlForApi = imageUrl
    const isDataUrl = imageUrl.startsWith('data:')
    console.log('[analyzeFlashCrop] Is data URL:', isDataUrl)
    
    if (isDataUrl) {
      const approximateSizeMB = (imageUrl.length * 0.75) / (1024 * 1024)
      console.log('[analyzeFlashCrop] Data URL size:', approximateSizeMB.toFixed(2), 'MB')
      console.log('[analyzeFlashCrop] Uploading to Firebase Storage first to avoid large request...')
      
      try {
        urlForApi = await uploadDataUrlToTempStorage(imageUrl)
        console.log('[analyzeFlashCrop] ✅ Image uploaded to temp storage, using URL for API call')
      } catch (uploadError) {
        console.error('[analyzeFlashCrop] ❌ Failed to upload to temp storage:', uploadError)
        throw new Error(`Failed to upload image for analysis: ${uploadError instanceof Error ? uploadError.message : String(uploadError)}`)
      }
    }
    
    // Call backend API for flash detection
    console.log('[analyzeFlashCrop] Calling detection API:', DETECT_FLASH_TATTOO_DESIGN_ENDPOINT)
    const requestBody = JSON.stringify({ imageUrl: urlForApi })
    console.log('[analyzeFlashCrop] Request body size:', (requestBody.length / 1024).toFixed(2), 'KB')
    
    let response: Response
    try {
      response = await fetch(DETECT_FLASH_TATTOO_DESIGN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody
      })
    } catch (fetchError) {
      console.error('[analyzeFlashCrop] ❌ Fetch request FAILED (network error):', fetchError)
      if (fetchError instanceof TypeError) {
        console.error('[analyzeFlashCrop] TypeError - this usually means:')
        console.error('  - Network error (no connection)')
        console.error('  - CORS blocked')
        console.error('  - DNS resolution failed')
        console.error('  - Server connection refused')
      }
      throw new Error(`Network error calling detection API: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`)
    }

    console.log('[analyzeFlashCrop] API response status:', response.status, response.statusText)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No response body')
      console.error('[analyzeFlashCrop] API error response body:', errorText)
      throw new Error(`Detection API failed: ${response.status} ${response.statusText}. Response: ${errorText.substring(0, 200)}`)
    }

    const data: BackendDetectionResponse = await response.json()
    console.log('[analyzeFlashCrop] API response data:', data)

    if (!data.bounding_box) {
      console.error('[analyzeFlashCrop] No bounding box in response - design not detected')
      throw new Error('No flash tattoo design detected in the image. Make sure the image contains a clear tattoo design.')
    }

    // Convert detection bbox [x1, y1, x2, y2] to our BoundingBox format
    const [x1, y1, x2, y2] = data.bounding_box
    console.log('[analyzeFlashCrop] Bounding box coordinates:', { x1, y1, x2, y2 })
    
    const boundingBox: BoundingBox = {
      x: x1,
      y: y1,
      width: x2 - x1,
      height: y2 - y1
    }
    console.log('[analyzeFlashCrop] Calculated bounding box:', boundingBox)

    // Calculate padding values
    const leftPadding = boundingBox.x
    const topPadding = boundingBox.y
    const rightPadding = imageDimensions.width - (boundingBox.x + boundingBox.width)
    const bottomPadding = imageDimensions.height - (boundingBox.y + boundingBox.height)

    // Minimum required padding (12% of image width)
    const minRequiredPadding = imageDimensions.width * 0.12

    // Check if each side has minimum padding
    const leftHasMinimum = leftPadding >= minRequiredPadding
    const rightHasMinimum = rightPadding >= minRequiredPadding
    const topHasMinimum = topPadding >= minRequiredPadding
    const bottomHasMinimum = bottomPadding >= minRequiredPadding

    // Calculate padding percentages
    const padding = {
      top: {
        value: topPadding,
        percentage: (topPadding / imageDimensions.height) * 100,
        isValid: topHasMinimum,
        needsMore: !topHasMinimum
      },
      right: {
        value: rightPadding,
        percentage: (rightPadding / imageDimensions.width) * 100,
        isValid: rightHasMinimum,
        needsMore: !rightHasMinimum
      },
      bottom: {
        value: bottomPadding,
        percentage: (bottomPadding / imageDimensions.height) * 100,
        isValid: bottomHasMinimum,
        needsMore: !bottomHasMinimum
      },
      left: {
        value: leftPadding,
        percentage: (leftPadding / imageDimensions.width) * 100,
        isValid: leftHasMinimum,
        needsMore: !leftHasMinimum
      }
    }

    // Overall validation - all sides must have minimum padding
    const isValid = leftHasMinimum && rightHasMinimum && topHasMinimum && bottomHasMinimum
    console.log('[analyzeFlashCrop] Padding validation:', {
      minRequiredPadding,
      leftPadding,
      rightPadding,
      topPadding,
      bottomPadding,
      leftHasMinimum,
      rightHasMinimum,
      topHasMinimum,
      bottomHasMinimum,
      isValid
    })

    // Calculate optimal crop if padding is valid
    let optimalCrop
    if (isValid) {
      // Optimal: bounding box should be 64% of final width, with 36% total x-padding (18% each side)
      // This means: finalWidth = boundingBox.width / 0.64
      const optimalFinalWidth = boundingBox.width / 0.64
      const totalOptimalXPadding = optimalFinalWidth * 0.36
      const optimalXPaddingPerSide = totalOptimalXPadding / 2

      // For Y: use optimal final width to calculate height with 50% total y-padding (25% each side)
      const totalOptimalYPadding = optimalFinalWidth * 0.50
      const optimalYPaddingPerSide = totalOptimalYPadding / 2

      // Check if we can apply optimal padding (both sides need to have enough padding)
      const canApplyOptimalXPadding = leftPadding >= optimalXPaddingPerSide && rightPadding >= optimalXPaddingPerSide
      const canApplyOptimalYPadding = topPadding >= optimalYPaddingPerSide && bottomPadding >= optimalYPaddingPerSide

      // Calculate actual padding to use (center the design if we need to crop)
      let finalXPaddingPerSide = optimalXPaddingPerSide
      let finalYPaddingPerSide = optimalYPaddingPerSide

      // If we don't have enough padding on both sides for X, use the minimum available
      if (!canApplyOptimalXPadding) {
        const availableXPadding = Math.min(leftPadding, rightPadding)
        finalXPaddingPerSide = availableXPadding
      }

      // If we don't have enough padding on both sides for Y, use the minimum available
      if (!canApplyOptimalYPadding) {
        const availableYPadding = Math.min(topPadding, bottomPadding)
        finalYPaddingPerSide = availableYPadding
      }

      // Calculate crop coordinates (centered)
      const cropX = boundingBox.x - finalXPaddingPerSide
      const cropY = boundingBox.y - finalYPaddingPerSide
      const cropWidth = boundingBox.width + (finalXPaddingPerSide * 2)
      const cropHeight = boundingBox.height + (finalYPaddingPerSide * 2)

      optimalCrop = {
        x: Math.max(0, cropX),
        y: Math.max(0, cropY),
        width: Math.min(cropWidth, imageDimensions.width),
        height: Math.min(cropHeight, imageDimensions.height),
        appliedPadding: {
          top: finalYPaddingPerSide,
          right: finalXPaddingPerSide,
          bottom: finalYPaddingPerSide,
          left: finalXPaddingPerSide
        }
      }
    }

    const result = {
      isValid,
      boundingBox,
      imageDimensions,
      padding,
      confidence: 1.0, // Backend API doesn't return confidence, assume high confidence if detection succeeded
      optimalCrop
    }
    console.log('[analyzeFlashCrop] ✅ Analysis complete:', result)
    return result

  } catch (error) {
    console.error('[analyzeFlashCrop] ❌ Flash crop analysis failed:', error)
    if (error instanceof Error) {
      console.error('[analyzeFlashCrop] Error message:', error.message)
      console.error('[analyzeFlashCrop] Error stack:', error.stack)
    }
    throw error instanceof Error ? error : new Error('Flash crop analysis failed')
  }
}

/**
 * Generate padding overlay data for visualization with trapezoidal shapes to avoid overlaps
 */
export function generatePaddingOverlay(analysis: FlashCropAnalysis) {
  const { boundingBox, imageDimensions, padding } = analysis
  
  return {
    // Top padding trapezoid (full width at top, cut at bounding box)
    top: {
      type: 'trapezoid' as const,
      points: [
        { x: 0, y: 0 },
        { x: imageDimensions.width, y: 0 },
        { x: boundingBox.x + boundingBox.width, y: boundingBox.y },
        { x: boundingBox.x, y: boundingBox.y }
      ],
      isValid: padding.top.isValid
    },
    // Right padding trapezoid (full height at right, cut at bounding box)
    right: {
      type: 'trapezoid' as const,
      points: [
        { x: boundingBox.x + boundingBox.width, y: boundingBox.y },
        { x: imageDimensions.width, y: 0 },
        { x: imageDimensions.width, y: imageDimensions.height },
        { x: boundingBox.x + boundingBox.width, y: boundingBox.y + boundingBox.height }
      ],
      isValid: padding.right.isValid
    },
    // Bottom padding trapezoid (full width at bottom, cut at bounding box)
    bottom: {
      type: 'trapezoid' as const,
      points: [
        { x: boundingBox.x, y: boundingBox.y + boundingBox.height },
        { x: boundingBox.x + boundingBox.width, y: boundingBox.y + boundingBox.height },
        { x: imageDimensions.width, y: imageDimensions.height },
        { x: 0, y: imageDimensions.height }
      ],
      isValid: padding.bottom.isValid
    },
    // Left padding trapezoid (full height at left, cut at bounding box)
    left: {
      type: 'trapezoid' as const,
      points: [
        { x: 0, y: 0 },
        { x: boundingBox.x, y: boundingBox.y },
        { x: boundingBox.x, y: boundingBox.y + boundingBox.height },
        { x: 0, y: imageDimensions.height }
      ],
      isValid: padding.left.isValid
    }
  }
}
